import moment from 'moment';
import { Core4MetricAverage, Record, Result, Season, Sport } from '../../model/Entities';
import { GenderType } from '../../model/Types';
import { baseballCore4Averages } from './baseball/PlayerCore4BaseballAverages';

class PlayerCore4ScoreService {
    getTier = (score: number | undefined): number => {
        let tier: number;
        if (score === undefined || score < 60) {
            tier = 1;
        } else if (score < 90) {
            tier = 2;
        } else if (score < 105) {
            tier = 3;
        } else if (score < 120) {
            tier = 4;
        } else {
            tier = 5;
        }

        return tier;
    };

    getScore = (
        sport: Sport,
        season: Season,
        birthdateAsNumber: number,
        gender: GenderType,
        records: Record[],
        results: Result[],
    ): number | undefined => {
        const categories = this.getCategories(sport);

        // calculate category scores
        const categoryScores: number[] = categories
            .map((category) =>
                this.getCategoryScore(sport, season, birthdateAsNumber, gender, category, records, results),
            )
            .filter((categoryScore): categoryScore is number => categoryScore !== undefined);

        // calculate score average
        let categoryScoreAverage: number | undefined;
        if (categoryScores.length > 0) {
            categoryScoreAverage = categoryScores.reduce((acc, score) => acc + score, 0) / categoryScores.length;
        }

        return categoryScoreAverage;
    };

    getCategoryScore = (
        sport: Sport,
        season: Season,
        birthdateAsNumber: number,
        gender: GenderType,
        metricCategoryCode: string,
        records: Record[],
        results: Result[],
    ): number | undefined => {
        const metricScores: number[] = records
            .map((record) => {
                // get metric from sport
                const metric = sport.stations
                    ?.flatMap((station) => station.metrics)
                    .find((metric) => metric?.id === record.metricId);

                // get metric score
                let metricScore: number | undefined;
                if (metric && metric.code) {
                    // get record date
                    const result = results.find((r) => r.metricId === record.metricId && r.result === record.result);

                    // get metric average for record metric
                    const metricAverage = this.getMetricAverage(
                        sport,
                        season,
                        birthdateAsNumber,
                        gender,
                        metric.code,
                        result?.date,
                    );

                    // calculate metric score for record metric if it belongs to record metric category
                    if (metricAverage && metricAverage.metric.parentCode === metricCategoryCode) {
                        metricScore = this.getMetricScore(metricAverage, record.result);
                    }
                }

                return metricScore;
            })
            .filter((metricScore): metricScore is number => metricScore !== undefined);

        // calculate metric score category average
        let metricScoreAverage: number | undefined;
        if (metricScores.length > 0) {
            metricScoreAverage = metricScores.reduce((acc, score) => acc + score, 0) / metricScores.length;
        }

        return metricScoreAverage;
    };

    getMetricAverage = (
        sport: Sport,
        season: Season,
        birthdateAsNumber: number,
        gender: GenderType,
        metricCode: string,
        recordDateAsNumber?: number,
    ): Core4MetricAverage | undefined => {
        let core4MetricAverage: Core4MetricAverage | undefined;

        if (birthdateAsNumber !== undefined && season.startDate) {
            const birthdate = moment.utc(birthdateAsNumber).local();

            // get player age on record
            let age: number;
            if (recordDateAsNumber) {
                const recordDate = moment.utc(recordDateAsNumber).local();
                age = recordDate.diff(birthdate, 'years');
            } else {
                const seasonStartDate = moment.utc(season.startDate).local();
                age = seasonStartDate.diff(birthdate, 'years');
            }

            // get core metric average
            switch (sport.code) {
                case 'BASEBALL':
                    core4MetricAverage = baseballCore4Averages
                        // .filter((c4a) => c4a.gender === gender) // TODO: commented until we have gender values
                        .filter((c4a) => c4a.metric.code === metricCode)
                        .filter((c4a) => c4a.age.minAge <= age && age <= c4a.age.maxAge)[0];
                    break;
                default:
                    break;
            }
        }

        return core4MetricAverage;
    };

    getMetricScore = (
        core4MetricAverage: Core4MetricAverage | undefined,
        result: number | undefined,
    ): number | undefined => {
        let score: number | undefined;
        if (core4MetricAverage && result !== undefined) {
            if (core4MetricAverage && core4MetricAverage.metric.best === 'max') {
                score = (result / core4MetricAverage.average) * 100;
            } else if (core4MetricAverage && core4MetricAverage.metric.best === 'min') {
                score = (core4MetricAverage.average / result) * 100;
            }
        }

        return score;
    };

    getCategories = (sport: Sport): string[] => {
        let categories: string[] = [];
        switch (sport.code) {
            case 'BASEBALL':
                categories = baseballCore4Averages.map((c4a) => c4a.metric.parentCode);
                break;
            default:
                break;
        }

        return Array.from(new Set(categories));
    };
}

const playerCore4ScoreService: PlayerCore4ScoreService = new PlayerCore4ScoreService();
export default playerCore4ScoreService;
